import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { Icon, LinkButton, Logo } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { dashboardFor, paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

/** Labels and order come from the Figma header, not from the old public shell. */
const NAV = [
  { to: paths.home, label: 'Home', end: true },
  { to: paths.forStudents, label: 'For Students' },
  { to: paths.forCompanies, label: 'For Startups' },
  { to: paths.about, label: 'About Us' },
  { to: paths.blog, label: 'Blog' },
  { to: `${paths.home}#faqs`, label: 'FAQs' },
];

/**
 * The marketing header.
 *
 * Sits *over* the page rather than above it: in the design the hero gradient
 * runs edge to edge behind the nav, so the header carries no background of its
 * own and whatever renders beneath it has to start at the top of the viewport.
 * It stays put as the page scrolls, and once the page has moved under it the
 * bar frosts -- a blur only, no wash of colour -- and draws in: a shorter
 * bar and a smaller mark, so it takes less of the page it is now covering.
 *
 * The nav is centred on the viewport, not between the logo and the buttons --
 * hence a three-column grid with equal outer tracks instead of
 * `justify-between`, which would drift the nav sideways as either side changes
 * width (it does: the buttons swap when someone signs in).
 */
export function SiteHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const scrolled = useScrolled();
  // Home runs its hero backdrop up behind the nav; every other page sets
  // the bar on the night ground, so the spot lights stop at its edge.
  const onHome = useLocation().pathname === paths.home;
  const { isAuthenticated, user } = useAuth();
  // Signed in, the one thing worth offering is the way back in: "Log In" and
  // "Get Started" both lead to screens that would only bounce this person on.
  const dashboard = isAuthenticated ? dashboardFor(user?.role) : null;

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter] duration-300',
        (scrolled || isOpen) && 'backdrop-blur-xl',
        !scrolled && !isOpen && !onHome && 'bg-night',
      )}
    >
      <div
        className={cn(
          'wonder-container grid grid-cols-[1fr_auto_1fr] items-center transition-[height] duration-300',
          scrolled ? 'h-14 lg:h-16' : 'h-16 lg:h-20',
        )}
      >
        <Link
          to={paths.home}
          aria-label="Wonder — home"
          className={cn(
            'text-on-night origin-left justify-self-start transition-transform duration-300',
            scrolled ? 'scale-[0.7]' : 'scale-[0.85]',
          )}
        >
          <Logo />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-8 2xl:gap-10" aria-label="Main">
          {NAV.map((item) => (
            <HeaderLink key={item.label} {...item} />
          ))}
        </nav>

        <div className="col-start-3 hidden items-center gap-3 justify-self-end lg:flex">
          {dashboard ? (
            <LinkButton to={dashboard} variant="light" shape="pill" size="pillSm">
              Dashboard
              <Icon name="arrowRight" size={16} />
            </LinkButton>
          ) : (
            <>
              <LinkButton to={paths.login} variant="outlineLight" shape="pill" size="pillSm">
                Log In
                <Icon name="arrowRight" size={16} />
              </LinkButton>
              <LinkButton to={paths.registerStudent} variant="light" shape="pill" size="pillSm">
                Get Started
                <Icon name="arrowRight" size={16} />
              </LinkButton>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-controls="site-menu"
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          className="text-on-night col-start-3 justify-self-end lg:hidden"
        >
          <Icon name={isOpen ? 'close' : 'menu'} size={24} />
        </button>
      </div>

      {isOpen && (
        <div
          id="site-menu"
          className="bg-night-deep/95 border-night-line border-y backdrop-blur lg:hidden"
        >
          <nav className="wonder-container flex flex-col py-4" aria-label="Main">
            {NAV.map((item) => (
              <HeaderLink
                key={item.label}
                {...item}
                className="py-2"
                onClick={() => setIsOpen(false)}
              />
            ))}
            <div className="mt-4 flex flex-col gap-2">
              {dashboard ? (
                <LinkButton
                  to={dashboard}
                  variant="light"
                  shape="pill"
                  size="pillSm"
                  className="w-full"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </LinkButton>
              ) : (
                <>
                  <LinkButton
                    to={paths.login}
                    variant="outlineLight"
                    shape="pill"
                    size="pillSm"
                    className="w-full"
                  >
                    Log In
                  </LinkButton>
                  <LinkButton
                    to={paths.registerStudent}
                    variant="light"
                    shape="pill"
                    size="pillSm"
                    className="w-full"
                  >
                    Get Started
                  </LinkButton>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

/** Whether the page has scrolled past its top. */
function useScrolled() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  return scrolled;
}

const LINK = 'text-[0.9375rem] transition';
const IDLE = 'text-on-night hover:text-nav-active';
const ACTIVE = 'text-nav-active';

/**
 * One item of the main navigation.
 *
 * Items that point at a real route are `NavLink`s, so the active item is
 * highlighted and announced as `aria-current="page"` without this component
 * tracking the location itself. As more of the nav becomes real pages, moving
 * an entry is a matter of dropping the `#` from its target -- nothing here
 * changes.
 *
 * Items that are still in-page anchors are plain links. `NavLink` matches on
 * pathname alone, so on the home page every `/#...` entry would match `/` and
 * the whole bar would light up at once -- and each would claim to be the
 * current page to a screen reader. An anchor is a jump within a page, not a
 * destination, so it never takes the active treatment.
 */
function HeaderLink({ to, label, end, className, onClick }) {
  if (to.includes('#')) {
    return (
      <Link to={to} onClick={onClick} className={cn(LINK, IDLE, className)}>
        {label}
      </Link>
    );
  }

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => cn(LINK, isActive ? ACTIVE : IDLE, className)}
    >
      {label}
    </NavLink>
  );
}
